import {
    Button,
    ButtonBase,
    Divider,
    Grid,
    Grow,
    Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { observer } from "mobx-react";
import React, { FunctionComponent, useState } from "react";
import { BeatLoader } from "react-spinners";
import { Action } from "../../../../../models/Actions/Action";
import { EvalReviewActionMetadata } from "../../../../../models/Actions/Evaluation/EvalReviewActionMetadata";
import theme from "../../../../../Theme/AppTheme";
import {
    parseFromISO,
    standardDateTimeFormat,
} from "../../../../../utils/DateTimeUtils";
import { useStore } from "../../../../../utils/useStore";
import AcxMainTextField from "../../../../UI/AcxMainTextFieldGrid";
import { EvalStore } from "../../../Stores/EvalStore";

interface OwnProps {
    response: Action<EvalReviewActionMetadata>;
    level: number;
    responseIndex: number;
    enableComments: boolean;
    totalResponses: number;
}

type Props = OwnProps;

const ResponseDetails: FunctionComponent<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = observer((props) => {
    const store = useStore(EvalStore);
    const [loading, setLoading] = useState<boolean>(false);
    const [reply, setReply] = useState<string>("");
    const [addingResponse, setAddingResponse] = useState(false);

    function onReplyChange(evt: React.ChangeEvent<HTMLInputElement>) {
        setReply(evt.currentTarget.value);
    }

    function canReplyTo() {
        return (
            props.enableComments &&
            props.response.userId !== store.user?.profile.sub &&
            props.response.actionMetadata.actionType !== "ReviewComment" &&
            !addingResponse
        );
    }

    async function onSubmitReply() {
        setLoading(true);
        store.replyToCompleteReviewAction(props.response, reply).finally(() => {
            setReply("");
            setAddingResponse(false);
            setLoading(false);
        });
    }

    return (
        <>
            <Grid
                container
                justifyContent={"flex-start"}
                sx={{
                    paddingTop: 2,
                    marginBottom: 1.5,
                    paddingLeft: props.level * 5.5,
                }}
            >
                <Grid item container xs={12} spacing={1} alignItems={"center"}>
                    <Grid item>
                        <Typography
                            sx={{ fontSize: "12px" }}
                            variant={"subtitle2"}
                            color={"textSecondary"}
                        >
                            {props.response.actionMetadata.actionType ===
                            "ReviewComment" ? (
                                <>Reply</>
                            ) : (
                                <>Response</>
                            )}{" "}
                            by {props.response.user.userName},{" "}
                            {standardDateTimeFormat(
                                parseFromISO(props.response.createdOn!),
                            )}
                        </Typography>
                    </Grid>
                    {canReplyTo() && (
                        <Grid item>
                            <Button
                                sx={{
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    fontSize: "12px",
                                }}
                                variant={"text"}
                                color={"secondary"}
                                onClick={(event) => setAddingResponse(true)}
                            >
                                Reply
                            </Button>
                        </Grid>
                    )}

                    {Boolean(addingResponse && loading) && (
                        <Grid item>
                            <BeatLoader
                                size={9}
                                color={theme.palette.secondary.main}
                                loading={loading}
                            />
                        </Grid>
                    )}
                </Grid>

                <Grid container sx={{ marginTop: 2 }}>
                    <Grid item lg={6} md={8} xs={11}>
                        <Typography>
                            {props.response.actionMetadata.parsedMetadata.notes}
                        </Typography>
                    </Grid>

                    {addingResponse && (
                        <>
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    marginTop: 1,
                                    marginBottom: 1,
                                }}
                                justifyContent={"flex-start"}
                                container
                                wrap={"nowrap"}
                            >
                                <Grid item lg={6} md={8} xs={11}>
                                    <AcxMainTextField
                                        onChange={onReplyChange}
                                        labelText={`Reply to ${props.response.user.userName}`}
                                        multiLine
                                        rows={2}
                                        rowsMax={3}
                                        placeholderText={""}
                                        id="response-reply-notes-action-id"
                                        value={reply ?? ""}
                                    />

                                    <Grid
                                        container
                                        justifyContent={"flex-end"}
                                        sx={{
                                            marginTop: "8px",
                                            maxHeight: "36px",
                                        }}
                                    >
                                        <Grid item>
                                            <ButtonBase
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "bold",
                                                    textTransform: "none",
                                                    color: reply
                                                        ? theme.palette.primary
                                                              .main
                                                        : theme.palette.text
                                                              .disabled,
                                                }}
                                                disabled={!reply}
                                                onClick={onSubmitReply}
                                            >
                                                <Grow
                                                    in={!!reply}
                                                    timeout={400}
                                                >
                                                    <SendIcon
                                                        sx={{ marginRight: 2 }}
                                                    />
                                                </Grow>{" "}
                                                Reply
                                            </ButtonBase>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Grid>
            {props.responseIndex < props.totalResponses - 1 && (
                <Divider variant={"fullWidth"} />
            )}
        </>
    );
});

export default ResponseDetails;
